import i18n from "@/i18n";
import useHttpClient from "@/services/shared/useHttpClient";
import type { User } from "@/models/user";
import { STORES, useStorageService } from "@/services/infrastructure/storageService";
import { UserRole } from "@/models/userRole";
import { redirectToLogout } from "@/utils/redirectUtil";
import { LoginResponse } from "@/models/loginResponse";
import { LoginRequest } from "@/models/loginRequest";
import { localStorageService } from "@/services/infrastructure/localstorageService";

const BASE_URL = "/api/authentication";
const { get, post } = useHttpClient();
const { clearKey, store } = useStorageService<string>(STORES.language_key);

interface UseAuthenticationServiceReturn {
  getUser: () => Promise<User | undefined>;
  setUser: (user: User) => void;
  cleanUser: () => void;
  redirectToLogOut: () => Promise<void>;
  isAdministrator: () => Promise<boolean>;
  isGroupManager: () => Promise<boolean>;
  login: (credentials: LoginRequest) => Promise<void>;
}

const setLocale = (user: User) => {
  const userLocale = user.language.split("-")[0].toLocaleLowerCase();
  i18n.global.locale.value = userLocale;
  store(userLocale);
};
let isAuthenticated = false;
let currentUser: User | undefined;

export default (): UseAuthenticationServiceReturn => {
  const getUser = async (): Promise<User | undefined> => {
    if (currentUser && isAuthenticated) {
      return currentUser;
    }
    const user = await get<User>(`/api/authentication/user`).catch(() => null);

    if (user) {
      isAuthenticated = true;
      currentUser = user;
      localStorageService.setItem(STORES.user, currentUser);
      setLocale(user);
      return currentUser;
    }

    isAuthenticated = false;
    currentUser = undefined;
    clearKey();
  };
  const setUser = (user: User) => {
    localStorageService.setItem(STORES.user, JSON.stringify(user));
  };
  
  const redirectToLogOut = async (): Promise<void> => {
    try {
      await post<void>(`${BASE_URL}/logout`, {});
      LogoutAndClearData();

    } catch (error) {
      LogoutAndClearData();
    }
  };
  const LogoutAndClearData = () =>{
    clearKey();
    isAuthenticated = false;
    currentUser = undefined;
    localStorageService.removeItem("token");
    localStorageService.removeItem("refreshToken");
    localStorageService.removeItem(STORES.user);
    redirectToLogout();
  }
  const checkRole = async (role: UserRole): Promise<boolean> => {
    const user = await getUser();
    return !!user && user.role === role;
  };
  const cleanUser = (): void => {
    currentUser = undefined;
    isAuthenticated = false;
  };
  const isAdministrator = async (): Promise<boolean> => checkRole(UserRole.Administrator);
  const isGroupManager = async (): Promise<boolean> => {
    const user = await getUser();
    return !!user && user.isGroupManager;
  };

  const login = async (credentials: LoginRequest): Promise<void> => {

    const response = await post<LoginResponse>(`${BASE_URL}/login`, credentials);
    if (response) {
      isAuthenticated = true;
      localStorageService.setItem("token", response.token);
      localStorageService.setItem("refreshToken", response.refreshToken);
      await getUser();

    } else {
      throw new Error("Invalid login credentials");
    }

  }

  return { getUser, setUser, redirectToLogOut, isAdministrator, isGroupManager, cleanUser, login };
};